import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  INTAKE_ASSESSMENT_KIND_LABELS,
  INTAKE_ASSESSMENT_STATUS_LABELS,
  useIntakeAssessmentDetail,
  useIntakeAssessmentTransitions,
  useUpdateIntakeAssessmentRecord,
  type IntakeAssessmentRecordDto,
  type IntakeAssessmentStatus,
} from '@/hooks/useIntakeAssessment';
import {
  INTAKE_ASSESSMENT_PERMISSIONS,
  canSeeIntakeAssessmentMenu,
  hasPermission,
} from '@/lib/intakeAssessmentPermissions';
import { IntakeAssessmentPayloadFormSlot } from './IntakeAssessmentPayloadForm';
import {
  createDefaultFormState,
  parsePayloadToFormState,
  serializeFormState,
  validateFormState,
  type IntakeAssessmentPayloadFormState,
  type PayloadFormParseResult,
} from './intakeAssessmentPayloadFormModel';

const STATUS_BADGE_COLORS: Record<IntakeAssessmentStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  changes_requested: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-zinc-200 text-zinc-700',
};

const EDITABLE_STATUSES: ReadonlySet<IntakeAssessmentStatus> = new Set([
  'draft',
  'changes_requested',
]);

interface PayloadEditState {
  /** Current discriminated form state shown in the structured editor. */
  form: IntakeAssessmentPayloadFormState;
  /**
   * Compatibility warning for unmappable legacy payloads. When set, the
   * UI must block saving until the operator explicitly resets to the
   * shown defaults — silent overwrite of the original blob is not
   * allowed.
   */
  legacyWarning: {
    reason: string;
    rawPreview: string;
  } | null;
  /** True after the operator clicks the explicit reset action. */
  acknowledgedLegacy: boolean;
}

function buildPayloadEditState(
  recordKind: IntakeAssessmentRecordDto['recordKind'],
  raw: string,
): PayloadEditState {
  const parsed: PayloadFormParseResult = parsePayloadToFormState(
    recordKind,
    raw,
  );
  if (parsed.kind === 'ok') {
    return { form: parsed.state, legacyWarning: null, acknowledgedLegacy: false };
  }
  return {
    form: parsed.defaults,
    legacyWarning: {
      reason: parsed.reason,
      rawPreview: previewRaw(parsed.raw),
    },
    acknowledgedLegacy: false,
  };
}

function previewRaw(raw: string): string {
  const trimmed = raw.length > 200 ? `${raw.slice(0, 200)}…` : raw;
  return trimmed;
}

export default function IntakeAssessmentDetailPage() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const recordId = params.id;
  const { permissions } = useAuth();

  const { record, reviews, loading, error, fetchRecord, setRecord } =
    useIntakeAssessmentDetail();
  const update = useUpdateIntakeAssessmentRecord();
  const transitions = useIntakeAssessmentTransitions();

  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editOwner, setEditOwner] = useState('');
  const [payloadEdit, setPayloadEdit] = useState<PayloadEditState>(() => ({
    form: createDefaultFormState('golden_analysis'),
    legacyWarning: null,
    acknowledgedLegacy: false,
  }));

  const [approvedSummary, setApprovedSummary] = useState('');
  const [approveComment, setApproveComment] = useState('');
  const [changesComment, setChangesComment] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const canRead = canSeeIntakeAssessmentMenu(permissions);
  const canUpdate = hasPermission(
    permissions,
    INTAKE_ASSESSMENT_PERMISSIONS.UPDATE,
  );
  const canReview = hasPermission(
    permissions,
    INTAKE_ASSESSMENT_PERMISSIONS.REVIEW,
  );

  useEffect(() => {
    if (!recordId || !canRead) return;
    void fetchRecord(recordId);
  }, [recordId, canRead, fetchRecord]);

  useEffect(() => {
    if (record) {
      setEditTitle(record.title);
      setEditSummary(record.summary ?? '');
      setEditOwner(record.ownerUserId);
      setPayloadEdit(buildPayloadEditState(record.recordKind, record.payload));
      setApprovedSummary(record.approvedSummary ?? record.summary ?? '');
      setApproveComment('');
      setChangesComment('');
      setCancelReason('');
    }
  }, [record]);

  const fieldErrors = useMemo(
    () => validateFormState(payloadEdit.form),
    [payloadEdit.form],
  );

  if (!canRead) {
    return <AccessDenied resource="接诊评估" />;
  }

  if (loading && !record) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>
    );
  }
  if (!record) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        记录不存在或无权访问。
        <button
          className="ml-2 text-primary hover:underline"
          onClick={() => navigate('/intake-assessment/records')}
        >
          返回列表
        </button>
      </div>
    );
  }

  const editable = EDITABLE_STATUSES.has(record.status);
  const saveBlockedByLegacy =
    payloadEdit.legacyWarning !== null && !payloadEdit.acknowledgedLegacy;

  const updateLocal = (next: IntakeAssessmentRecordDto | null) => {
    if (next) setRecord(next);
  };

  const onAcknowledgeLegacy = () => {
    setPayloadEdit((prev) => ({ ...prev, acknowledgedLegacy: true }));
    toast.message(
      '已重置为结构化默认值；保存后将覆盖原始历史详情，请确认无误后再点击“保存编辑”。',
    );
  };

  const onSaveEdits = async () => {
    if (!editable) {
      toast.error('当前状态不允许编辑');
      return;
    }
    if (saveBlockedByLegacy) {
      toast.error(
        '历史详情格式无法安全映射，请先点击“重置为结构化默认值”确认后再保存。',
      );
      return;
    }
    if (fieldErrors.length > 0) {
      toast.error(fieldErrors[0].message);
      return;
    }
    const payload = serializeFormState(payloadEdit.form);
    const result = await update.updateRecord(record.id, {
      expectedVersion: record.version,
      title: editTitle.trim(),
      summary: editSummary.trim(),
      payload,
      ownerUserId: editOwner.trim() || undefined,
    });
    if (result) {
      toast.success('已保存');
      updateLocal(result);
    } else if (update.error) {
      toast.error(update.error.message);
    }
  };

  const onSubmit = async () => {
    const result = await transitions.submit(record.id, {
      expectedVersion: record.version,
    });
    if (result) {
      toast.success('已提交审核');
      updateLocal(result);
      void fetchRecord(record.id);
    } else if (transitions.error) {
      toast.error(transitions.error.message);
    }
  };

  const onApprove = async () => {
    if (!approvedSummary.trim()) {
      toast.error('请填写批准摘要');
      return;
    }
    const result = await transitions.approve(record.id, {
      expectedVersion: record.version,
      approvedSummary: approvedSummary.trim(),
      reviewerComment: approveComment.trim() || undefined,
    });
    if (result) {
      toast.success('已批准');
      updateLocal(result);
      void fetchRecord(record.id);
    } else if (transitions.error) {
      toast.error(transitions.error.message);
    }
  };

  const onRequestChanges = async () => {
    if (!changesComment.trim()) {
      toast.error('请填写打回原因');
      return;
    }
    const result = await transitions.requestChanges(record.id, {
      expectedVersion: record.version,
      reviewerComment: changesComment.trim(),
    });
    if (result) {
      toast.success('已打回修改');
      updateLocal(result);
      void fetchRecord(record.id);
    } else if (transitions.error) {
      toast.error(transitions.error.message);
    }
  };

  const onCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('请填写作废原因');
      return;
    }
    const result = await transitions.cancel(record.id, {
      expectedVersion: record.version,
      reason: cancelReason.trim(),
    });
    if (result) {
      toast.success('已作废');
      updateLocal(result);
      void fetchRecord(record.id);
    } else if (transitions.error) {
      toast.error(transitions.error.message);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            className="text-sm text-muted-foreground hover:underline"
            onClick={() => navigate('/intake-assessment/records')}
          >
            ← 返回列表
          </button>
          <h1 className="text-xl font-semibold">
            {INTAKE_ASSESSMENT_KIND_LABELS[record.recordKind]} · {record.title}
          </h1>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-xs ${STATUS_BADGE_COLORS[record.status]}`}
        >
          {INTAKE_ASSESSMENT_STATUS_LABELS[record.status]}
        </span>
      </div>

      {error && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          {error.message}
        </Card>
      )}

      <Card className="p-4">
        <h2 className="mb-3 font-medium">基础信息</h2>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">客户 ID</dt>
          <dd className="font-mono text-xs">{record.customerId}</dd>
          <dt className="text-muted-foreground">负责人</dt>
          <dd>{record.ownerUserId}</dd>
          <dt className="text-muted-foreground">创建者</dt>
          <dd>{record.createdBy}</dd>
          <dt className="text-muted-foreground">创建时间</dt>
          <dd>{new Date(record.createdAt).toLocaleString('zh-CN')}</dd>
          <dt className="text-muted-foreground">最近更新</dt>
          <dd>{new Date(record.updatedAt).toLocaleString('zh-CN')}</dd>
          <dt className="text-muted-foreground">版本</dt>
          <dd>{record.version}</dd>
          {record.submittedAt && (
            <>
              <dt className="text-muted-foreground">提交时间</dt>
              <dd>{new Date(record.submittedAt).toLocaleString('zh-CN')}</dd>
            </>
          )}
          {record.reviewedAt && (
            <>
              <dt className="text-muted-foreground">审核时间</dt>
              <dd>
                {new Date(record.reviewedAt).toLocaleString('zh-CN')}{' '}
                {record.reviewedBy ? `· ${record.reviewedBy}` : ''}
              </dd>
            </>
          )}
          {record.cancelledAt && (
            <>
              <dt className="text-muted-foreground">作废时间</dt>
              <dd>
                {new Date(record.cancelledAt).toLocaleString('zh-CN')}{' '}
                {record.cancelledBy ? `· ${record.cancelledBy}` : ''}
              </dd>
            </>
          )}
        </dl>
        {record.reviewerComment && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <div className="font-medium">最近审核意见</div>
            <div className="whitespace-pre-wrap">{record.reviewerComment}</div>
          </div>
        )}
        {record.cancelReason && (
          <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
            <div className="font-medium">作废原因</div>
            <div className="whitespace-pre-wrap">{record.cancelReason}</div>
          </div>
        )}
      </Card>

      {record.status === 'approved' && record.approvedSummary && (
        <Card className="border-green-200 bg-green-50 p-4">
          <h2 className="mb-2 font-medium text-green-900">已批准摘要（方案模块可见）</h2>
          <p className="whitespace-pre-wrap text-sm text-green-900">
            {record.approvedSummary}
          </p>
          {record.approvedAt && (
            <p className="mt-2 text-xs text-green-800">
              批准于 {new Date(record.approvedAt).toLocaleString('zh-CN')} ·{' '}
              {record.approvedBy}
            </p>
          )}
        </Card>
      )}

      <Card className="p-4">
        <h2 className="mb-3 font-medium">记录内容</h2>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="d-title">标题</Label>
            <Input
              id="d-title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              disabled={!editable || !canUpdate}
              maxLength={255}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="d-summary">摘要</Label>
            <textarea
              id="d-summary"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              rows={2}
              value={editSummary}
              onChange={(e) => setEditSummary(e.target.value)}
              disabled={!editable || !canUpdate}
              maxLength={1000}
            />
          </div>
          {payloadEdit.legacyWarning && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              role="alert"
              data-testid="legacy-payload-warning"
            >
              <div className="font-medium">历史详情兼容性提示</div>
              <p className="mt-1">{payloadEdit.legacyWarning.reason}</p>
              <p className="mt-1 text-xs">
                此条历史详情无法安全映射到结构化表单。原始内容已保留在后端，保存编辑前
                请先确认是否要使用下方默认结构覆盖。
              </p>
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer">查看原始详情预览</summary>
                <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-amber-100 p-2 font-mono">
                  {payloadEdit.legacyWarning.rawPreview}
                </pre>
              </details>
              {editable && canUpdate && !payloadEdit.acknowledgedLegacy && (
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onAcknowledgeLegacy}
                  >
                    重置为结构化默认值
                  </Button>
                </div>
              )}
              {payloadEdit.acknowledgedLegacy && (
                <p className="mt-2 text-xs font-medium text-amber-900">
                  已确认；保存后将以下方结构化数据覆盖原始历史详情。
                </p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">
              详情 · {INTAKE_ASSESSMENT_KIND_LABELS[record.recordKind]}
            </h3>
            <IntakeAssessmentPayloadFormSlot
              state={payloadEdit.form}
              onChange={(form: IntakeAssessmentPayloadFormState) =>
                setPayloadEdit((prev) => ({ ...prev, form }))
              }
              disabled={
                !editable ||
                !canUpdate ||
                (payloadEdit.legacyWarning !== null &&
                  !payloadEdit.acknowledgedLegacy)
              }
              errors={fieldErrors}
              idPrefix="ia-detail-payload"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="d-owner">负责人 ID</Label>
            <Input
              id="d-owner"
              value={editOwner}
              onChange={(e) => setEditOwner(e.target.value)}
              disabled={!editable || !canUpdate}
            />
          </div>
          {editable && canUpdate && (
            <div className="flex gap-2">
              <Button
                onClick={() => void onSaveEdits()}
                disabled={update.loading || saveBlockedByLegacy}
              >
                {update.loading ? '保存中…' : '保存编辑'}
              </Button>
              {saveBlockedByLegacy && (
                <span className="self-center text-xs text-amber-700">
                  历史详情未确认重置，暂不可保存。
                </span>
              )}
            </div>
          )}
        </div>
      </Card>

      {canUpdate && (record.status === 'draft' || record.status === 'changes_requested') && (
        <Card className="p-4">
          <h2 className="mb-3 font-medium">提交审核</h2>
          <Button
            onClick={() => void onSubmit()}
            disabled={transitions.loading}
          >
            {transitions.loading ? '提交中…' : '提交审核'}
          </Button>
        </Card>
      )}

      {canReview && record.status === 'submitted' && (
        <Card className="p-4">
          <h2 className="mb-3 font-medium">审核决定</h2>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="r-summary">批准摘要（方案模块将引用）*</Label>
              <textarea
                id="r-summary"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
                value={approvedSummary}
                onChange={(e) => setApprovedSummary(e.target.value)}
                maxLength={2000}
                placeholder="精简结论，避免 PHI 明文；不得包含诊断或疗效承诺。"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="r-approve-comment">审核意见（可选）</Label>
              <textarea
                id="r-approve-comment"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={2}
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                maxLength={2000}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void onApprove()} disabled={transitions.loading}>
                批准
              </Button>
            </div>
            <hr className="my-2" />
            <div className="space-y-1">
              <Label htmlFor="r-changes-comment">打回修改原因 *</Label>
              <textarea
                id="r-changes-comment"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={2}
                value={changesComment}
                onChange={(e) => setChangesComment(e.target.value)}
                maxLength={2000}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => void onRequestChanges()}
              disabled={transitions.loading}
            >
              打回修改
            </Button>
          </div>
        </Card>
      )}

      {canUpdate && record.status !== 'approved' && record.status !== 'cancelled' && (
        <Card className="p-4">
          <h2 className="mb-3 font-medium">作废记录</h2>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="r-cancel">作废原因 *</Label>
              <textarea
                id="r-cancel"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={2}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                maxLength={2000}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => void onCancel()}
              disabled={transitions.loading}
            >
              作废
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <h2 className="mb-3 font-medium">审核历史</h2>
        {reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无审核记录</p>
        ) : (
          <ul className="space-y-2">
            {reviews.map((r) => (
              <li key={r.id} className="rounded-md border p-3 text-sm">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {INTAKE_ASSESSMENT_STATUS_LABELS[r.fromStatus]} →{' '}
                    {INTAKE_ASSESSMENT_STATUS_LABELS[r.toStatus]}
                  </span>
                  <span>{new Date(r.reviewedAt).toLocaleString('zh-CN')}</span>
                </div>
                <div className="mt-1">
                  <span className="font-medium">
                    {r.decision === 'approved' ? '批准' : '打回修改'}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {r.reviewedBy}
                  </span>
                </div>
                {r.reviewerComment && (
                  <p className="mt-1 whitespace-pre-wrap text-sm">
                    {r.reviewerComment}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
